import * as React from 'react';
import cn from 'clsx';
import styles from '@sicredi/styles/_tabs.scss';

import Icon from '../icon/Icon';
import { hash } from '../utils';
import { Tooltip } from '../tooltip';

import Item, { Props as ItemProps } from './Item';

export interface Schema {
  children?: React.ReactNode;
  onChange?: (index: number) => void;
  className?: string;
}

export interface AriaLabelProps extends Schema {
  'aria-label': string;
}

export interface AriaLabelledbybyProps extends Schema {
  'aria-labelledby': string;
}

export type Props = AriaLabelProps | AriaLabelledbybyProps;

export interface CompoundedAccordion extends React.FC<Props> {
  Item: React.FC<ItemProps>;
}

const Tabs: CompoundedAccordion = ({
  onChange,
  children,
  className,
  ...props
}) => {
  const tabs = React.Children.toArray(children) as React.ReactElement<
    ItemProps
  >[];

  const selectedTabCandidate = tabs.findIndex(({ props }) => !props.disabled);
  const selectedTab = tabs.findIndex(({ props }) => !!props.selected);

  const [selected, setSelected] = React.useState(
    selectedTab === -1 ? selectedTabCandidate : selectedTab
  );

  const handleClick = (i: number) => {
    setSelected(i);

    onChange && onChange(i);
  };

  return (
    <div
      role="group"
      className={cn(styles['sicredi-tabs'], className)}
      aria-label={props['aria-label'] || undefined}
      aria-labelledby={props['aria-labelledby'] || undefined}
    >
      <ul data-tabs="" role="tablist">
        {tabs.map(({ props }, i: number) => (
          <li data-item="" key={hash(props.label)} role="heading" aria-level={3}>
            <button
              type="button"
              id={`${hash(props.label)}-tab`}
              role="tab"
              onClick={() => handleClick(i)}
              disabled={props.disabled}
              tabIndex={props.disabled ? -1 : 0}
              aria-disabled={props.disabled ? 'true' : 'false'}
              aria-controls={
                props.disabled ? undefined : `${hash(props.label)}-tab-content`
              }
              aria-selected={
                i === (selectedTab === -1 ? selected : selectedTab)
              }
              data-testid={
                props['data-testid'] ? `${props['data-testid']}` : undefined
              }
              data-button=""
            >
              {props.label}
              {props.disabled && props.disabledTip && " "}
              {props.disabled && props.disabledTip && (
                <Tooltip tip={props.disabledTip} placement={props.placementTip || "bottom"} allowInDisabledElement>
                  <Icon name="feedback-info" appearance="primary" />
                </Tooltip>
              )}
            </button>
          </li>
        ))}
      </ul>

      {tabs.map(
        (component, i: number) =>
          !component.props.disabled &&
          React.cloneElement(component, {
            selected: i === (selectedTab === -1 ? selected : selectedTab),
            'data-testid': component.props['data-testid']
              ? `${component.props['data-testid']}-content`
              : undefined,
          })
      )}
    </div>
  );
};

Tabs.Item = Item;

export default Tabs;
